// /src/components/imovel/cadastro/FormControls.jsx
// VERSÃO 11.0 SUPREMA - DATA-DRIVEN & MULTI-RENDER SYNC

import React, { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { FiChevronUp, FiShield, FiCheck, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// --- 🛰️ 1. SEÇÃO DE FORMULÁRIO (GABINETE ADAPTATIVO) ---
export function FormSection({ title, children, defaultOpen = true, icon: Icon = FiShield }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="w-full border rounded-[3.5rem] overflow-hidden transition-all duration-700 shadow-2xl group/section"
             style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
            
            <button 
                type="button" onClick={() => setIsOpen(!isOpen)} 
                className="w-full flex justify-between items-center p-10 transition-all border-b bg-white/[0.01] hover:bg-white/[0.02]"
                style={{ borderColor: 'var(--border-color)' }}
            >
                <div className="flex items-center gap-8">
                    <div className="w-16 h-16 rounded-[1.5rem] border flex items-center justify-center shadow-inner group-hover/section:scale-110 transition-transform duration-500"
                         style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)', color: 'var(--accent-color)' }}>
                        <Icon size={28} className={isOpen ? 'animate-pulse' : ''} />
                    </div>
                    <div className="text-left">
                        <h3 className="text-2xl font-black uppercase tracking-tighter text-auto">
                            {title}
                        </h3>
                        <p className="text-[9px] font-black uppercase tracking-[0.5em] mt-1.5 opacity-30 text-accent">
                            Módulo de Processamento Master
                        </p>
                    </div>
                </div>
                <div className="transition-all duration-500" style={{ color: 'var(--accent-color)', opacity: isOpen ? 1 : 0.3 }}>
                    <motion.div animate={{ rotate: isOpen ? 0 : 180 }} transition={{ duration: 0.4 }}>
                        <FiChevronUp size={28} />
                    </motion.div>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
                        className="overflow-hidden"
                    >
                        <div className="p-12 md:p-20 w-full relative">
                            <div className="absolute -right-20 -bottom-20 w-64 h-64 opacity-5 blur-[100px] pointer-events-none" 
                                 style={{ backgroundColor: 'var(--accent-color)' }}></div>
                            
                            <div className="relative z-10 w-full">
                                {children}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// --- 📝 2. INPUT PADRÃO (INDUSTRIAL ELITE) ---
export function InputField({ label, name, value, onChange, type = 'text', placeholder, required = false, className = '' }) {
    return (
        <div className={`space-y-4 group ${className}`}>
            {label && (
                <label className="text-[10px] font-black uppercase tracking-[0.3em] ml-2 transition-all opacity-40 group-focus-within:opacity-100 text-auto">
                    {label} {required && <span className="text-accent">*</span>}
                </label>
            )}
            <input 
                type={type} name={name} value={value} onChange={onChange} 
                placeholder={placeholder} required={required}
                className="w-full h-16 px-8 rounded-2xl outline-none border font-bold text-sm transition-all duration-500 shadow-inner placeholder:opacity-20 bg-main-auto text-auto border-auto focus:border-accent"
            />
        </div>
    );
}

// --- 🎯 3. SELECT DE ELITE (UPGRADE HÍBRIDO) ---
export function SelectField({ label, name, value, onChange, options = [], children, required = false, className = '', placeholder = "Selecione uma opção..." }) {
    return (
        <div className={`space-y-4 group ${className}`}>
            {label && (
                <label className="text-[10px] font-black uppercase tracking-[0.3em] ml-2 opacity-40 group-focus-within:opacity-100 transition-all text-auto">
                    {label} {required && <span className="text-accent">*</span>}
                </label>
            )}
            <div className="relative">
                <select 
                    name={name} 
                    value={value} 
                    onChange={onChange} 
                    required={required} 
                    className="w-full h-16 px-8 rounded-2xl outline-none border font-bold text-sm appearance-none cursor-pointer transition-all shadow-inner bg-main-auto text-auto border-auto focus:border-accent"
                >
                    {/* Placeholder dinâmico */}
                    {placeholder && <option value="">{placeholder}</option>}
                    
                    {/* 🚀 LÓGICA DE RENDERIZAÇÃO INTELIGENTE */}
                    {options && options.length > 0 ? (
                        options.map((opt, index) => (
                            <option key={`${name}-opt-${index}`} value={opt.value}>
                                {opt.label}
                            </option>
                        ))
                    ) : (
                        children
                    )}
                </select>
                <FiChevronRight className="absolute right-6 top-1/2 -translate-y-1/2 rotate-90 opacity-20 pointer-events-none group-hover:opacity-100 transition-all text-accent" />
            </div>
        </div>
    );
}

// --- 💹 4. CAMPO MONETÁRIO ---
export function CurrencyField({ label, name, value, onValueChange, placeholder, required = false, className = '' }) {
    return (
        <div className={`space-y-4 group ${className}`}>
            <label className="text-[10px] font-black uppercase tracking-[0.3em] ml-2 opacity-40 group-focus-within:opacity-100 transition-all text-auto">
                {label} {required && <span className="text-accent">*</span>}
            </label>
            <NumericFormat
                name={name}
                value={value}
                // 🛡️ TRAVA DE SEGURANÇA: Só dispara se onValueChange for uma função válida
                onValueChange={(values) => {
                    if (typeof onValueChange === 'function') {
                        onValueChange(name, values.floatValue || 0);
                    } else {
                        console.warn(`🚩 [FRONT-WARN]: onValueChange não definida para o campo ${name}`);
                    }
                }} 
                placeholder={placeholder}
                required={required}
                thousandSeparator="."
                decimalSeparator=","
                prefix="R$ "
                allowNegative={false}
                className="w-full h-20 px-8 rounded-[2rem] outline-none border font-black text-3xl tracking-tighter transition-all shadow-2xl bg-main-auto text-accent border-auto focus:border-accent"
            />
        </div>
    );
}
// --- 🛡️ 5. GRUPO DE CHECKBOX (GRID INDUSTRIAL) ---
export function CheckboxGroup({ options, selectedOptions, onChange, className = '' }) {
    return (
        <div className={`col-span-full grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 ${className}`}>
            {options.map(option => {
                const isSelected = selectedOptions.includes(option);
                return (
                    <label key={option} className="flex items-center gap-4 cursor-pointer p-6 rounded-[1.5rem] border transition-all duration-300 hover:scale-[1.02] group/item shadow-xl"
                           style={{ 
                               backgroundColor: isSelected ? 'var(--bg-card)' : 'var(--bg-main)', 
                               borderColor: isSelected ? 'var(--accent-color)' : 'var(--border-color)' 
                           }}>
                        <div className="relative flex items-center justify-center">
                            <input 
                                type="checkbox" checked={isSelected} 
                                onChange={() => onChange(option)} className="peer hidden" 
                            />
                            <div className="w-7 h-7 rounded-xl border-2 flex items-center justify-center transition-all peer-checked:border-none shadow-inner"
                                 style={{ 
                                     borderColor: 'var(--border-color)', 
                                     backgroundColor: isSelected ? 'var(--accent-color)' : 'transparent' 
                                 }}>
                                <FiCheck style={{ color: 'var(--bg-main)' }} 
                                         className={`scale-0 ${isSelected ? 'scale-100' : ''} transition-transform`} 
                                         size={16} strokeWidth={4} />
                            </div>
                        </div>
                        <span className={`text-[11px] font-black uppercase tracking-tight transition-colors text-auto`}
                              style={{ opacity: isSelected ? 1 : 0.3 }}>
                            {option}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}

// --- 📁 6. CHECKBOX FIELD INDIVIDUAL ---
export function CheckboxField({ label, name, checked, onChange, required = false, className = '' }) {
    return (
        <div className={`${className} group`}>
            <label className="flex items-center space-x-4 cursor-pointer">
                <div className="relative flex items-center justify-center">
                    <input
                        type="checkbox" name={name} checked={checked} onChange={onChange} required={required}
                        className="peer appearance-none h-8 w-8 rounded-xl border transition-all duration-300 border-auto bg-main-auto checked:bg-accent"
                    />
                    <FiCheck 
                        className="absolute opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                        style={{ color: 'var(--bg-main)' }}
                        size={18}
                        strokeWidth={4}
                    />
                </div>
                <span className="text-[11px] font-black uppercase tracking-[0.2em] transition-all text-auto"
                      style={{ opacity: checked ? 1 : 0.3 }}>
                    {label} {required && <span className="text-accent">*</span>}
                </span>
            </label>
        </div>
    );
}

// --- ✍️ 7. TEXTAREA ---
export function TextareaField({ label, name, value, onChange, placeholder, rows = 6, className = '' }) {
    return (
        <div className={`space-y-4 group ${className}`}>
            {label && (
                <label className="text-[10px] font-black uppercase tracking-[0.3em] ml-2 opacity-40 transition-all group-focus-within:opacity-100 text-auto">{label}</label>
            )}
            <textarea 
                name={name} value={value} onChange={onChange} placeholder={placeholder} rows={rows} 
                className="w-full p-8 rounded-[2.5rem] outline-none border font-medium text-base leading-relaxed transition-all resize-none shadow-inner custom-scrollbar bg-main-auto text-auto border-auto focus:border-accent"
            />
        </div>
    );
}

export const HiddenField = ({ name, value }) => <input type="hidden" name={name} value={value} />;